import { createContext, useContext } from 'react';
import { unformatDecimal } from '@paradigm/utils/formatDecimal';
import { GrfqQuotes, GrfqLeg } from '#/unified-rfqs/entities/grfq/domain';
import { GrfqBbo } from '#/unified-rfqs/entities/grfq/bbo';
import { getProduct } from '#/products/domain';
import { isStandardProduct } from '#/products/types';
import { quantityMeetsStrategyMinSize } from '#/unified-rfqs/ui/rfqs/rfqs-functions';

import { LegTableView } from '#/unified-rfqs/ui/types';

import { QuoteBuilderState } from './form-reducer';
import timerView from './views/timer';
import quantityView from './views/quantity';
import priceView from './views/price';
import legsView from './views/legs';
import validationView from './views/validation';
import legsPricesView, { LegsPricesView } from './views/legsPrices';
import quotesTableView, { QuotesTableView } from './views/quotesTable';
import bestAskBidQuoteView, {
  BestAskBidQuoteView,
} from './views/bestAskBidQuote';

interface FormOption<T> {
  readonly value: T;
  readonly label: string;
  readonly isDefault?: boolean;
}

export interface View {
  readonly timer: {
    readonly options: readonly FormOption<string>[];
    readonly selectedOption?: FormOption<string>;
  };

  readonly price: {
    readonly value: string;
    readonly precision: number;
  };
  readonly quantity: {
    readonly value: string;
    readonly precision: number;
    readonly error: string | undefined;
  };

  readonly account: string | undefined;
  readonly legs: readonly LegTableView[];
  readonly legsPrices: LegsPricesView;
  readonly quotesTable: QuotesTableView;
  readonly bestQuote: BestAskBidQuoteView;

  readonly fieldsValidation: {
    readonly price: boolean;
    readonly quantity: boolean;
  };

  readonly isValid: boolean;

  readonly isPrefillingQuotes: boolean;
  readonly isPostOnly: boolean;

  readonly side: 'BUY' | 'SELL';

  readonly isSingleLeg: boolean;
  readonly haveOptions: boolean;
  readonly isMultiLegOption: boolean;
  readonly markPriceIv?: string;
}

export const ViewCtx = createContext<View | null>(null);

ViewCtx.displayName = 'QuoteBuilderView';

export function useQuoteBuilderView() {
  const ctx = useContext(ViewCtx);
  if (ctx !== null) return ctx;
  throw new Error('Missing QuoteBuilderProvider upwards in this tree.');
}

interface ViewParams {
  readonly bbo: GrfqBbo | null;
  readonly legPricingEnabled: boolean;
  readonly state: QuoteBuilderState;
  readonly legs: readonly GrfqLeg[] | undefined;
  readonly quotes: GrfqQuotes;
  readonly myDesk: string;
  readonly isPrefillingQuotes: boolean;
  readonly isLegPricesLoading: boolean;
  readonly productCode: string | undefined;
  readonly isSingleLeg: boolean;
  readonly haveOptions: boolean;
}

export function createView(params: ViewParams): View {
  const {
    bbo,
    state,
    legs = [],
    quotes,
    myDesk,
    legPricingEnabled,
    isPrefillingQuotes,
    isLegPricesLoading,
    productCode,
    isSingleLeg,
    haveOptions,
  } = params;
  const { account, price, quantity, quoteTimer, legsPrice, isPostOnly, side } =
    state;

  const fieldsValidation = validationView({
    quantity,
    legPricingEnabled,
    price,
    legs,
    legsPrice,
    side,
    productCode,
  });

  return {
    timer: timerView(quoteTimer),
    side,
    account,
    isPostOnly,
    quantity: quantityView(quantity, legs),
    price: priceView({
      price,
      legPricingEnabled,
      legsPrice,
      legs,
      side,
    }),
    legs: legsView(quantity, legs, side),
    legsPrices: legsPricesView(legsPrice, bbo, legs, isLegPricesLoading, side),
    bestQuote: bestAskBidQuoteView(bbo, legs),
    quotesTable: quotesTableView({
      myDesk,
      bidQuotes: quotes.bids,
      askQuotes: quotes.asks,
    }),
    fieldsValidation,
    isValid:
      fieldsValidation.price &&
      fieldsValidation.quantity &&
      quantityMeetsMinSize(productCode, quantity, bbo, legs) &&
      account != null &&
      account !== '',
    isPrefillingQuotes,
    isSingleLeg,
    haveOptions,
    isMultiLegOption: !isSingleLeg && haveOptions,
    markPriceIv: bbo?.leg_prices[0]?.mark_price_iv ?? undefined,
  };
}

/**
 * Quantity must meet the strategy product's min block size. For linear
 * futures that floor is a USDC notional, converted to a coin floor via the
 * per-unit notional across legs (NOT the strategy mark, which nets to ~0 for
 * multi-leg spreads). Shares quantityMeetsStrategyMinSize with the DRFQ gate.
 */
function quantityMeetsMinSize(
  productCode: string | undefined,
  quantity: string,
  bbo: GrfqBbo | null,
  legs: readonly GrfqLeg[],
): boolean {
  if (productCode == null) return true;
  const product = getProduct(productCode);
  if (!isStandardProduct(product)) return true;

  // Per-leg marks keyed by instrument name (GRFQ bbo exposes leg_prices).
  const markByInstrument = new Map(
    (bbo?.leg_prices ?? []).map((leg) => [
      leg.instrument,
      Number(leg.mark_price) || 0,
    ]),
  );
  const normalizedLegs = legs.map((leg) => ({
    ratio: Number(leg.ratio) || 0,
    markPrice: markByInstrument.get(leg.instrument) ?? 0,
  }));
  const fallbackMark = bbo?.mark_price != null ? Number(bbo.mark_price) : null;

  return quantityMeetsStrategyMinSize(
    product,
    Number(unformatDecimal(quantity) || '0'),
    normalizedLegs,
    fallbackMark,
  );
}
